import React, {PropsWithChildren, useEffect} from 'react';
import {WithMsalProps} from '@azure/msal-react';
import EnumContext, {IEnum} from './enum-context';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import {EnumerationClient} from '../Api/EnumerationClients';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import {
    WorksLocationReply,
    PhaseTypeReply,
    TmTypeReply,
    FwClosureReply,
    PermitStatusReply, NoticeStatusReply, NoticeFileTypeReply, PermitTypesReply, RoadTypesReply, CancelReasonReply, SubResourcesReply, RegStatusReply, SenstiveHoursReply
} from '../Api/EnumerationTypes';

type Props = WithMsalProps & {
    children: React.ReactNode;
};

const EnumProvider: React.FC<Props> = (props: PropsWithChildren<Props>) => {
    const [enums, setEnums] = React.useState<IEnum | null>(null);

    useEffect(() => {
        async function fetchValues() {
            const enumerationClient = new NSwagAdapter<EnumerationClient>(new EnumerationClient(
                new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                    new SafeDigAccessTokenProvider(props.msalContext))));

            //Read workslocations data
            const workLocationResult = await enumerationClient.callWithReply(
                WorksLocationReply, (client) => client.getWorksLocations());
            workLocationResult.throwIfFailed();

            //Get Phase Types
            const phaseTypeResult = await enumerationClient.callWithReply(
                PhaseTypeReply, (client) => client.getPhaseTypes());
            phaseTypeResult.throwIfFailed();

            //Get Tm Types
            const tmTypeResult = await enumerationClient.callWithReply(
                TmTypeReply, (client) => client.getTmTypes());
            tmTypeResult.throwIfFailed();

            //Get Footway Closures
            const fwClosureResult = await enumerationClient.callWithReply(
                FwClosureReply, (client) => client.getFwClosures());
            fwClosureResult.throwIfFailed();

            //Get Permit Statuses
            const permitResult = await enumerationClient.callWithReply(
                PermitStatusReply, (client) => client.getPermitStatuses());
            permitResult.throwIfFailed();

            //Get Notice Statuses
            const noticeResult = await enumerationClient.callWithReply(
                NoticeStatusReply, (client) => client.getNoticeStatuses());
            noticeResult.throwIfFailed();

            //Get Notice File Types
            const fileTypeResult = await enumerationClient.callWithReply(
                NoticeFileTypeReply, (client) => client.getNoticeFileTypes());
            fileTypeResult.throwIfFailed();

            const permitTypeResult = await enumerationClient.callWithReply(
                PermitTypesReply, (client) => client.getPermitTypes());
            permitTypeResult.throwIfFailed();

            const roadTypeResult = await enumerationClient.callWithReply(
                RoadTypesReply, (client) => client.getRoadTypes());
            roadTypeResult.throwIfFailed();

            const cancelReasonsResult = await enumerationClient.callWithReply(
                CancelReasonReply, (client) => client.getCancelReasons());
            cancelReasonsResult.throwIfFailed();

            const subResourcesResult = await enumerationClient.callWithReply(
                SubResourcesReply, (client) => client.getSubResources());
            subResourcesResult.throwIfFailed();

            const regStatusResult = await enumerationClient.callWithReply(
                RegStatusReply, (client) => client.getRegStatuses());
            regStatusResult.throwIfFailed();

            const senstiveHoursResult = await enumerationClient.callWithReply(
                SenstiveHoursReply, (client) => client.getSensitiveHoursOptions());
            senstiveHoursResult.throwIfFailed();

            setEnums({
                worksLocationTypes: workLocationResult.reply!.worksLocations,
                phaseTypes: phaseTypeResult.reply!.phaseTypes,
                tmTypes: tmTypeResult.reply!.tmTypes,
                fwClosures: fwClosureResult.reply!.fwClosures,
                permitStates: permitResult.reply!.permitStatuses,
                noticeStates: noticeResult.reply!.noticeStatuses,
                noticeFileTypes: fileTypeResult.reply!.fileTypes,
                permitTypes: permitTypeResult.reply!.permitTypes,
                roadTypes: roadTypeResult.reply!.roadTypes,
                cancelReasons: cancelReasonsResult.reply!.cancelReasons,
                subResources: subResourcesResult.reply!.subResources,
                regStatuses: regStatusResult.reply!.registrationStatuses,
                sensitiveHours: senstiveHoursResult.reply!.sensitiveHoursOptions
            });
        }

        fetchValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        enums,
        setEnums
    };

    return (
        <EnumContext.Provider value={value}>
            {props.children}
        </EnumContext.Provider>
    );
}

export default EnumProvider;
